import { Either, flow, Runtime } from "fp";
import React from "react";
import * as ReactQuery from "react-query";
import { QueryKey } from "react-query";

import { Company } from "~/Company";
import * as Context from "~/Context";
import * as Data from "~/Data";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Time } from "~/Time";

export namespace SuggestedPairing {
  export type Suggestion = {
    readonly id: number;
    readonly driverId: number;
    readonly assetId: number;
    readonly recognitionDate: string;
    readonly acceptanceDate: string | null;
    readonly expirationDate: string | null;
    readonly creationDate: string;
    readonly promptDate: string | null;
    readonly rejectionDate: string | null;
    readonly deletionDate: string | null;
  };

  export type SuggestionsResponse = {
    readonly suggestions: readonly Suggestion[];
    readonly total: number;
    readonly page: number;
    readonly pageSize: number;
  };

  export type QueryParams = {
    readonly company: Data.Reference.Reference;
    readonly startDate: string;
    readonly endDate: string;
    readonly active?: boolean;
    readonly page?: number;
    readonly pageSize?: number;
  };

  export const DEFAULT_PAGE_SIZE = 1000;
  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        data: Runtime.array(
          Runtime.strict({
            suggested_pairing_id: Runtime.number(),
            driver_id: Runtime.number(),
            asset_id: Runtime.number(),
            recognition_date: Runtime.string(),
            acceptance_date: Runtime.nullable(Runtime.string()),
            expiration_date: Runtime.nullable(Runtime.string()),
            creation_date: Runtime.string(),
            prompt_date: Runtime.nullable(Runtime.string()),
            rejection_date: Runtime.nullable(Runtime.string()),
            deletion_date: Runtime.nullable(Runtime.string()),
          })
        ),
        meta: Runtime.strict({
          items_per_page: Runtime.number(),
          total_items: Runtime.number(),
          current_page: Runtime.number(),
          total_pages: Runtime.number(),
        }),
      });

    type DecodableResponse = Runtime.ToType<typeof runtime>;

    export const decode = (
      response: DecodableResponse
    ): SuggestionsResponse => {
      // Filter out deleted suggestions
      const activeSuggestions = response.data
        .filter((s) => s.deletion_date === null)
        .map((s) => ({
          id: s.suggested_pairing_id,
          driverId: s.driver_id,
          assetId: s.asset_id,
          recognitionDate: s.recognition_date,
          acceptanceDate: s.acceptance_date ?? null,
          expirationDate: s.expiration_date ?? null,
          creationDate: s.creation_date,
          promptDate: s.prompt_date ?? null,
          rejectionDate: s.rejection_date ?? null,
          deletionDate: s.deletion_date ?? null,
        }));

      return {
        suggestions: activeSuggestions,
        total: response.meta.total_items,
        page: response.meta.current_page,
        pageSize: response.meta.items_per_page,
      };
    };
  }

  export namespace Query {
    export const tag = () => "Suggested Pairings" as const;

    export const key = ({
      company,
      startDate,
      endDate,
      active,
      page,
      pageSize,
    }: QueryParams): QueryKey => [
      tag(),
      [
        company.id,
        startDate,
        endDate,
        active?.toString() ?? "all",
        page?.toString() ?? "1",
        pageSize?.toString() ?? "50",
      ].join(":"),
    ];

    const toEndpoint = ({
      company,
      startDate,
      endDate,
      active,
      page,
      pageSize,
    }: QueryParams): string => {
      const params = new URLSearchParams();
      params.append("startDate", startDate);
      params.append("endDate", endDate);
      if (active !== undefined) {
        params.append("active", active.toString());
      }
      if (page !== undefined) {
        params.append("page", page.toString());
      } else {
        params.append("page", "1");
      }
      if (pageSize !== undefined) {
        params.append("pageSize", pageSize.toString());
      } else {
        params.append("pageSize", "50000");
      }
      return `/pairing/companies/${
        company.id
      }/suggestions?${params.toString()}`;
    };

    export const request = (
      params: QueryParams
    ): Request.Fn<SuggestionsResponse> => {
      const endpoint = toEndpoint(params);
      return flow(
        Request.fromConfig({ endpoint }),
        Request.Expect.status(200),
        Request.Expect.json(Response.runtime()),
        Request.onSuccess(Response.decode)
      );
    };

    export const use = (
      params: Omit<QueryParams, "company">
    ): Remote<SuggestionsResponse> => {
      const { company } = Company.use();
      const queryParams = { ...params, company };
      return Remote.Query.use(key(queryParams), request(queryParams), {
        // Don't use keepPreviousData since useAllSuggestedPairings accumulates pages
        // keepPreviousData would cause old query data to be re-processed when params change
        keepPreviousData: false,
        staleTime: Time.Duration.minutes(5).duration,
      });
    };
  }
}

export const useSuggestedPairings = SuggestedPairing.Query.use;

/**
 * Custom hook to fetch all pages of suggestions using React Query's useInfiniteQuery.
 * Automatically fetches subsequent pages until all suggestions are retrieved.
 */
export const useAllSuggestedPairings = (params: {
  readonly startDate: string;
  readonly endDate: string;
  readonly active: boolean;
  readonly pageSize?: number;
}): Remote<SuggestedPairing.SuggestionsResponse> => {
  const { company } = Company.use();
  const context = Context.useContext();
  const fetchPageSize = params.pageSize ?? SuggestedPairing.DEFAULT_PAGE_SIZE;

  const queryKey = SuggestedPairing.Query.key({
    company,
    startDate: params.startDate,
    endDate: params.endDate,
    active: params.active,
    pageSize: fetchPageSize,
    // Don't include page in key - infinite query manages pages internally
  });

  const infiniteQuery = ReactQuery.useInfiniteQuery<
    Either.ErrorOr<SuggestedPairing.SuggestionsResponse>,
    Error
  >(
    queryKey,
    ({ pageParam = 1 }) =>
      SuggestedPairing.Query.request({
        company,
        startDate: params.startDate,
        endDate: params.endDate,
        active: params.active,
        pageSize: fetchPageSize,
        page: pageParam as number,
      })(context)(),
    {
      // Don't use keepPreviousData - infinite query handles its own caching
      keepPreviousData: false,
      staleTime: Time.Duration.minutes(5).duration,
      getNextPageParam: (
        lastPage: Either.ErrorOr<SuggestedPairing.SuggestionsResponse>
      ) => {
        if (Either.isLeft(lastPage)) {
          return undefined;
        }
        const data = lastPage.right;
        const totalPages = Math.ceil(data.total / data.pageSize);
        return data.page < totalPages ? data.page + 1 : undefined;
      },
    }
  );

  // Automatically fetch next page when available
  React.useEffect(() => {
    // Only fetch if we're still mounted and have more pages
    let isMounted = true;

    if (
      isMounted &&
      infiniteQuery.hasNextPage &&
      !infiniteQuery.isFetchingNextPage
    ) {
      void infiniteQuery.fetchNextPage();
    }

    return () => {
      isMounted = false;
    };
  }, [
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.fetchNextPage,
  ]);

  // Convert infinite query result to Remote
  return React.useMemo(() => {
    if (infiniteQuery.error) {
      return Remote.error(infiniteQuery.error);
    }

    if (!infiniteQuery.data) {
      return Remote.loading();
    }

    // Check if still loading more pages (including when actively fetching)
    if (infiniteQuery.hasNextPage || infiniteQuery.isFetchingNextPage) {
      return Remote.loading();
    }

    // Flatten all pages and check for errors
    const pages = infiniteQuery.data.pages;
    const firstError = pages.find((page): page is Either.Left<Error> =>
      Either.isLeft(page)
    );

    if (firstError) {
      return Remote.error(firstError.left);
    }

    // All pages are successful, flatten into single response
    const successPages = pages.filter((page): page is Either.Right<
      SuggestedPairing.SuggestionsResponse
    > => Either.isRight(page));
    const allSuggestions = successPages.flatMap(
      (page) => page.right.suggestions
    );
    const firstPage = successPages[0]?.right;

    if (!firstPage) {
      return Remote.loading();
    }

    return Remote.success({
      suggestions: allSuggestions,
      total: firstPage.total,
      page: 1,
      pageSize: allSuggestions.length,
    });
  }, [
    infiniteQuery.data,
    infiniteQuery.error,
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
  ]);
};
